import { supabase } from './supabase';

export interface BookingMilestone {
  id: string;
  business_id: string;
  customer_id: string;
  booking_id: string;
  milestone: MilestoneData;
  created_at: string;
  updated_at: string;
}

export interface MilestoneData {
  type: 'booking_created' | 'booking_scheduled' | 'booking_started' | 'booking_completed' | 'booking_cancelled' | 'payment_received' | 'proposal_created' | 'custom';
  title: string;
  description?: string;
  timestamp: string;
  user_id?: string;
  metadata?: Record<string, any>;
}

// Create a booking milestone entry
export async function createBookingMilestone(
  businessId: string,
  customerId: string,
  bookingId: string,
  milestoneData: MilestoneData
): Promise<BookingMilestone> {
  if (!supabase) {
    throw new Error('Supabase client is not initialized');
  }

  // Validate required parameters
  if (!businessId || !customerId || !bookingId) {
    const missingFields = [];
    if (!businessId) missingFields.push('businessId');
    if (!customerId) missingFields.push('customerId');
    if (!bookingId) missingFields.push('bookingId');
    
    const error = new Error(`Missing required fields for milestone creation: ${missingFields.join(', ')}`);
    console.error('Error creating booking milestone:', error);
    throw error;
  }

  console.log('📝 Creating milestone with data:', {
    businessId,
    customerId,
    bookingId,
    milestoneType: milestoneData.type,
    milestoneTitle: milestoneData.title
  });

  const { data, error } = await supabase
    .from('booking_milestones')
    .insert([{
      business_id: businessId,
      customer_id: customerId,
      booking_id: bookingId,
      milestone: milestoneData
    }])
    .select()
    .single();

  if (error) {
    console.error('Error creating booking milestone:', error);
    console.error('Milestone data that failed:', {
      business_id: businessId,
      customer_id: customerId,
      booking_id: bookingId,
      milestone: milestoneData
    });
    throw error;
  }

  console.log('✅ Milestone created successfully:', data);
  return data as BookingMilestone;
}

// Get all milestones for a booking
export async function getBookingMilestones(bookingId: string): Promise<BookingMilestone[]> {
  if (!supabase) {
    throw new Error('Supabase client is not initialized');
  }

  const { data, error } = await supabase
    .from('booking_milestones')
    .select('*')
    .eq('booking_id', bookingId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Error fetching booking milestones:', error);
    throw error;
  }

  return data as BookingMilestone[];
}

// Get all milestones for a customer
export async function getCustomerMilestones(customerId: string): Promise<BookingMilestone[]> {
  if (!supabase) {
    throw new Error('Supabase client is not initialized');
  }

  const { data, error } = await supabase
    .from('booking_milestones')
    .select('*')
    .eq('customer_id', customerId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching customer milestones:', error);
    throw error;
  }

  return data as BookingMilestone[];
}

// Create a "booking created" milestone
export async function createBookingCreatedMilestone(
  businessId: string,
  customerId: string,
  bookingId: string,
  bookingTitle?: string
): Promise<BookingMilestone> {
  const milestoneData: MilestoneData = {
    type: 'booking_created',
    title: 'Booking Created',
    description: bookingTitle ? `Booking "${bookingTitle}" was created` : 'New booking was created',
    timestamp: new Date().toISOString(),
    metadata: {
      booking_title: bookingTitle
    }
  };

  return createBookingMilestone(businessId, customerId, bookingId, milestoneData);
}

// Create a "booking scheduled" milestone
export async function createBookingScheduledMilestone(
  businessId: string,
  customerId: string,
  bookingId: string,
  scheduledDate: string,
  scheduledTime: string
): Promise<BookingMilestone> {
  const milestoneData: MilestoneData = {
    type: 'booking_scheduled',
    title: 'Booking Scheduled',
    description: `Booking scheduled for ${scheduledDate} at ${scheduledTime}`,
    timestamp: new Date().toISOString(),
    metadata: {
      scheduled_date: scheduledDate,
      scheduled_time: scheduledTime
    }
  };

  return createBookingMilestone(businessId, customerId, bookingId, milestoneData);
}

// Create a "booking completed" milestone
export async function createBookingCompletedMilestone(
  businessId: string,
  customerId: string,
  bookingId: string,
  bookingTitle?: string,
  completedAt?: string
): Promise<BookingMilestone> {
  console.log('✅ Creating booking completed milestone with data:', {
    businessId,
    customerId, 
    bookingId,
    bookingTitle,
    completedAt
  });

  let description = 'Booking was completed';
  if (bookingTitle) {
    description = `Booking "${bookingTitle}" was completed`;
  }
  
  if (completedAt) {
    description += ` on ${new Date(completedAt).toLocaleDateString()}`;
  }

  const milestoneData: MilestoneData = {
    type: 'booking_completed',
    title: 'Booking Completed',
    description,
    timestamp: new Date().toISOString(),
    metadata: {
      booking_title: bookingTitle,
      completed_at: completedAt || new Date().toISOString()
    }
  };

  console.log('✅ Booking completed milestone data prepared:', milestoneData);

  try {
    const result = await createBookingMilestone(businessId, customerId, bookingId, milestoneData);
    console.log('✅ Booking completed milestone created successfully:', result);
    return result;
  } catch (error) {
    console.error('❌ Failed to create booking completed milestone:', error);
    throw error;
  }
}

// Create a custom milestone
export async function createCustomMilestone(
  businessId: string,
  customerId: string,
  bookingId: string,
  title: string,
  description?: string,
  metadata?: Record<string, any>
): Promise<BookingMilestone> {
  const milestoneData: MilestoneData = {
    type: 'custom',
    title,
    description,
    timestamp: new Date().toISOString(),
    metadata
  };

  return createBookingMilestone(businessId, customerId, bookingId, milestoneData);
}

// Create a "proposal created" milestone
export async function createProposalCreatedMilestone(
  businessId: string,
  customerId: string,
  proposalId: string,
  proposalTitle?: string
): Promise<BookingMilestone> {
  console.log('📝 Creating proposal milestone with data:', {
    businessId,
    customerId, 
    proposalId,
    proposalTitle
  });

  const milestoneData: MilestoneData = {
    type: 'proposal_created',
    title: 'Proposal Created',
    description: proposalTitle ? `Proposal "${proposalTitle}" was created` : 'New proposal was created',
    timestamp: new Date().toISOString(),
    metadata: {
      proposal_title: proposalTitle,
      entity_type: 'proposal' // To distinguish from actual bookings
    }
  };

  console.log('📝 Milestone data prepared:', milestoneData);

  try {
    // Using proposalId as booking_id since milestone table expects booking_id
    const result = await createBookingMilestone(businessId, customerId, proposalId, milestoneData);
    console.log('✅ Proposal milestone created successfully:', result);
    return result;
  } catch (error) {
    console.error('❌ Failed to create proposal milestone:', error);
    throw error;
  }
}

// Create a "proposal sent" milestone
export async function createProposalSentMilestone(
  businessId: string,
  customerId: string,
  proposalId: string,
  proposalTitle?: string,
  sentVia?: 'sms' | 'email' | 'both',
  recipient?: string
): Promise<BookingMilestone> {
  console.log('📤 Creating proposal sent milestone with data:', {
    businessId,
    customerId, 
    proposalId,
    proposalTitle,
    sentVia,
    recipient
  });

  let description = 'Proposal was sent';
  if (proposalTitle) {
    description = `Proposal "${proposalTitle}" was sent`;
  }
  
  if (sentVia && recipient) {
    const method = sentVia === 'both' ? 'via SMS and email' : `via ${sentVia.toUpperCase()}`;
    description += ` ${method} to ${recipient}`;
  }

  const milestoneData: MilestoneData = {
    type: 'custom', // Using custom type since we don't have a specific 'proposal_sent' type
    title: 'Proposal Sent',
    description,
    timestamp: new Date().toISOString(),
    metadata: {
      proposal_title: proposalTitle,
      entity_type: 'proposal',
      sent_via: sentVia,
      recipient: recipient
    }
  };

  console.log('📤 Proposal sent milestone data prepared:', milestoneData);

  try {
    // Using proposalId as booking_id since milestone table expects booking_id
    const result = await createBookingMilestone(businessId, customerId, proposalId, milestoneData);
    console.log('✅ Proposal sent milestone created successfully:', result);
    return result;
  } catch (error) {
    console.error('❌ Failed to create proposal sent milestone:', error);
    throw error;
  }
}

// Create a "proposal viewed" milestone
export async function createProposalViewedMilestone(
  businessId: string,
  customerId: string,
  proposalId: string,
  proposalTitle?: string,
  viewedAt?: string
): Promise<BookingMilestone> {
  console.log('👀 Creating proposal viewed milestone with data:', {
    businessId,
    customerId, 
    proposalId,
    proposalTitle,
    viewedAt
  });

  let description = 'Proposal was viewed by customer';
  if (proposalTitle) {
    description = `Proposal "${proposalTitle}" was viewed by customer`;
  }
  
  if (viewedAt) {
    description += ` at ${new Date(viewedAt).toLocaleString()}`;
  }

  const milestoneData: MilestoneData = {
    type: 'custom', // Using custom type since we don't have a specific 'proposal_viewed' type
    title: 'Proposal Viewed',
    description,
    timestamp: new Date().toISOString(),
    metadata: {
      proposal_title: proposalTitle,
      entity_type: 'proposal',
      viewed_at: viewedAt || new Date().toISOString()
    }
  };

  console.log('👀 Proposal viewed milestone data prepared:', milestoneData);

  try {
    // Using proposalId as booking_id since milestone table expects booking_id
    const result = await createBookingMilestone(businessId, customerId, proposalId, milestoneData);
    console.log('✅ Proposal viewed milestone created successfully:', result);
    return result;
  } catch (error) {
    console.error('❌ Failed to create proposal viewed milestone:', error);
    throw error;
  }
}

// Create a "proposal approved" milestone
export async function createProposalApprovedMilestone(
  businessId: string,
  customerId: string,
  proposalId: string,
  proposalTitle?: string,
  offerTitle?: string
): Promise<BookingMilestone> {
  console.log('✅ Creating proposal approved milestone with data:', {
    businessId,
    customerId, 
    proposalId,
    proposalTitle,
    offerTitle
  });

  let description = 'Proposal was approved by customer';
  if (proposalTitle && offerTitle) {
    description = `Proposal "${proposalTitle}" was approved - offer "${offerTitle}" accepted`;
  } else if (proposalTitle) {
    description = `Proposal "${proposalTitle}" was approved by customer`;
  }

  const milestoneData: MilestoneData = {
    type: 'custom', // Using custom type since we don't have a specific 'proposal_approved' type
    title: 'Proposal Approved',
    description,
    timestamp: new Date().toISOString(),
    metadata: {
      proposal_title: proposalTitle,
      entity_type: 'proposal',
      offer_title: offerTitle,
      approved_at: new Date().toISOString()
    }
  };

  console.log('✅ Proposal approved milestone data prepared:', milestoneData);

  try {
    // Using proposalId as booking_id since milestone table expects booking_id
    const result = await createBookingMilestone(businessId, customerId, proposalId, milestoneData);
    console.log('✅ Proposal approved milestone created successfully:', result);
    return result;
  } catch (error) {
    console.error('❌ Failed to create proposal approved milestone:', error);
    throw error;
  }
} 